import { usePromotions } from "../context/PromotionContext";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styles from "./Footer.module.css";
import { FaArrowUp, FaFacebookF, FaInstagram, FaTelegram } from "react-icons/fa";
import { IoCallOutline, IoMailOutline } from "react-icons/io5";
import applePayLogo from "../assets/payment-systems/apple-pay.svg";
import googlePayLogo from "../assets/payment-systems/google-pay.svg";
import mastercardLogo from "../assets/payment-systems/mastercard.svg";
import visaLogo from "../assets/payment-systems/visa.svg";
import { FOOTER_NAV_SECTIONS } from "../config/navigation";
import { useLoyaltySettings } from "../context/LoyaltySettingsContext";
import AuthCustomerModal from "./AuthCustomerModal";
import RegisterCustomerModal from "./RegisterCustomerModal";

const paymentLogos = [
  { label: "Visa", src: visaLogo },
  { label: "Mastercard", src: mastercardLogo },
  { label: "Google Pay", src: googlePayLogo },
  { label: "Apple Pay", src: applePayLogo },
];

function Footer() {
  const { visible: promotionsVisible } = usePromotions();
  const { settings: loyaltySettings } = useLoyaltySettings();
  const year = new Date().getFullYear();
  const location = useLocation();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);

  const scrollTop = () =>
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

  const handleOpenAuthModal = () => {
    localStorage.setItem(
      "lastVisitedPath",
      location.pathname + location.search,
    );
    setIsRegisterModalOpen(false);
    setIsAuthModalOpen(true);
  };

  const handleOpenRegisterModal = () => {
    localStorage.setItem(
      "lastVisitedPath",
      location.pathname + location.search,
    );
    setIsAuthModalOpen(false);
    setIsRegisterModalOpen(true);
  };

  const scrollToCurrentHashTarget = (path) => {
    if (!path.includes("#")) return;

    const [pathname, hash] = path.split("#");
    if (location.pathname !== pathname || location.hash !== `#${hash}`) {
      return;
    }

    window.requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  return (
    <footer className={styles.footer} aria-labelledby="footerHeading">
      <h2 id="footerHeading" className={styles.srOnly}>
        Підвал сайту MyZooShop
      </h2>

      <div className={styles.footerContent}>
        <section className={styles.brand} aria-label="Про бренд">
          <Link to="/" className={styles.brandName} aria-label="На головну">
            MyZooShop
          </Link>
          <p className={styles.brandTagline}>
            <span className={styles.taglineMain}>
              Магазин товарів для улюбленців:
            </span>{" "}
            <span className={styles.taglineSub}>
              турбота, якість, швидка доставка.
            </span>
          </p>

          <div className={styles.payments} aria-label="Платіжні системи">
            <ul className={styles.paymentIcons}>
              {paymentLogos.map(({ label, src }) => (
                <li key={label}>
                  <img
                    src={src}
                    alt={label}
                    className={styles.paymentIcon}
                    loading="lazy"
                    decoding="async"
                  />
                </li>
              ))}
            </ul>
            <p className={styles.paymentNote}>
              Приймаємо картки, онлайн-оплату та Apple/Google Pay.
            </p>
          </div>
        </section>

        <section
          className={styles.contactInfo}
          aria-label="Контактна інформація"
          itemScope
          itemType="https://schema.org/Organization"
        >
          <meta itemProp="name" content="MyZooShop" />
          <h3 className={styles.sectionTitle}>Контакти</h3>

          <ul className={styles.linksContact}>
            <li className={`${styles.contactRow} ${styles.phones}`}>
              <IoCallOutline className={styles.contactIcon} aria-hidden="true" />
              <div className={styles.phoneList} role="list">
                <a
                  className={styles.link}
                  href="tel:+380671234567"
                  itemProp="telephone"
                >
                  +38 (067) 123-45-67
                </a>
              </div>
            </li>

            <li className={styles.contactRow}>
              <IoMailOutline className={styles.contactIcon} aria-hidden="true" />
              <a
                className={styles.link}
                href="mailto:info@myzooshop.com.ua"
                itemProp="email"
              >
                info@myzooshop.com.ua
              </a>
            </li>
          </ul>
        </section>

        <div className={styles.footerLinkSectionsRow}>
          {FOOTER_NAV_SECTIONS.map((section) => (
            <nav
              key={section.title}
              className={styles.linkSection}
              aria-label={section.ariaLabel}
            >
              <h3 className={styles.sectionTitle}>{section.title}</h3>
              <ul className={styles.links}>
                {section.links.filter((link) => link.path !== "/promotions" || promotionsVisible).filter((link) => link.path !== "/bonus-program" || loyaltySettings?.visible).map((link) => (
                  <li key={link.path}>
                    {link.path === "/login" ? (
                      <button
                        type="button"
                        className={`${styles.link} ${styles.linkButton}`}
                        onClick={handleOpenAuthModal}
                      >
                        {link.label}
                      </button>
                    ) : link.path === "/register" ? (
                      <button
                        type="button"
                        className={`${styles.link} ${styles.linkButton}`}
                        onClick={handleOpenRegisterModal}
                      >
                        {link.label}
                      </button>
                    ) : (
                      <Link
                        className={styles.link}
                        to={link.path}
                        onClick={() => scrollToCurrentHashTarget(link.path)}
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
      </div>

      <section
        className={styles.socialSection}
        aria-label="Соціальні мережі MyZooShop"
      >
        <h3 className={styles.sectionTitle}>Ми в соціальних мережах</h3>
        <div className={styles.socialMedia} role="list">
          <span className={styles.socialItem} role="listitem" aria-label="Telegram">
            <FaTelegram className={styles.socialIcon} aria-hidden="true" />
          </span>
          <span className={styles.socialItem} role="listitem" aria-label="Instagram">
            <FaInstagram className={styles.socialIcon} aria-hidden="true" />
          </span>
          <span className={styles.socialItem} role="listitem" aria-label="Facebook">
            <FaFacebookF className={styles.socialIcon} aria-hidden="true" />
          </span>
        </div>
      </section>

      <div className={styles.footerBottom}>
        <p className={styles.copy}>
          &copy; 2025 - {year} myzooshop.com.ua.
          <br className={styles.mobBr} />
          Всі права захищені.
        </p>
        <button
          type="button"
          className={styles.topBtn}
          onClick={scrollTop}
          aria-label="Повернутися нагору"
          title="Нагору"
        >
          <FaArrowUp aria-hidden="true" />
        </button>
      </div>

      {isAuthModalOpen && (
        <AuthCustomerModal
          onClose={() => setIsAuthModalOpen(false)}
          onRegisterClick={() => {
            setIsAuthModalOpen(false);
            setIsRegisterModalOpen(true);
          }}
        />
      )}
      {isRegisterModalOpen && (
        <RegisterCustomerModal
          onClose={() => setIsRegisterModalOpen(false)}
          onLoginClick={() => {
            setIsRegisterModalOpen(false);
            setIsAuthModalOpen(true);
          }}
        />
      )}
    </footer>
  );
}

export default Footer;
